/*
 * Renders a group of checkboxes that can be used to select
 * multiple values from a set of options.
 *
 * The corresponding component when rendering the selected
 * values is PropertyGroup.
 *
 */

import React from 'react';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { FieldCheckbox, ValidationError, HelpText } from '../../components';

import css from './FieldCheckboxGroup.module.css';

const FieldCheckboxRenderer = props => {
  const {
    className,
    rootClassName,
    label,
    isRequired,
    helpText,
    optionLabelClassName,
    twoColumns,
    chips,
    sorted,
    id,
    fields,
    options,
    meta,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const Tag = label ? 'fieldset' : 'div';

  const displayOptions = sorted
    ? [...options].sort((a, b) => a.label.localeCompare(b.label))
    : options;

  if (chips) {
    const currentValues = fields.value || [];
    return (
      <Tag className={classes}>
        {label ? (
          <legend>
            {label}
            {isRequired ? <span className={css.requiredMark}>*</span> : null}
          </legend>
        ) : null}
        <HelpText helpText={helpText} />
        <div className={css.chipsRow}>
          {displayOptions.map(option => {
            const isSelected = currentValues.includes(option.key);
            return (
              <button
                key={option.key}
                type="button"
                className={classNames(css.chip, { [css.chipSelected]: isSelected })}
                onClick={() => {
                  if (isSelected) {
                    const idx = currentValues.indexOf(option.key);
                    fields.remove(idx);
                  } else {
                    fields.push(option.key);
                  }
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <ValidationError fieldMeta={{ ...meta }} />
      </Tag>
    );
  }

  const listClasses = twoColumns ? classNames(css.list, css.twoColumns) : css.list;

  return (
    <Tag className={classes}>
      {label ? (
        <legend>
          {label}
          {isRequired ? <span className={css.requiredMark}>*</span> : null}
        </legend>
      ) : null}
      <HelpText helpText={helpText} />
      <ul className={listClasses}>
        {displayOptions.map(option => {
          const fieldId = `${id}.${option.key}`;
          const textClassName = optionLabelClassName;
          const textClassNameMaybe = textClassName ? { textClassName } : {};
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={fields.name}
                label={option.label}
                value={option.key}
                {...textClassNameMaybe}
              />
            </li>
          );
        })}
      </ul>
      <ValidationError fieldMeta={{ ...meta }} />
    </Tag>
  );
};

// Note: name and component are required fields for FieldArray.
// Component-prop we define in this file, name needs to be passed in

/**
 * @typedef {Object} CheckboxGroupOption
 * @property {string} key
 * @property {string} label
 */

/**
 * Final Form Field containing checkbox group.
 * Renders a group of checkboxes that can be used to select
 * multiple values from a set of options.
 *
 * The corresponding component when rendering the selected
 * values is PropertyGroup.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name this is required for FieldArray (Final Form component)
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.optionLabelClassName given to each option
 * @param {string} props.id given to input
 * @param {ReactNode} props.label the label for the checkbox group
 * @param {Array<CheckboxGroupOption>} props.options E.g. [{ key, label }]
 * @param {boolean} props.twoColumns
 * @param {boolean} props.chips when true, renders options as pill-shaped chip buttons
 * @param {boolean} props.sorted when true, sorts options alphabetically by label
 * @returns {JSX.Element} Final Form Field containing multiple checkbox inputs
 */
const FieldCheckboxGroup = props => <FieldArray component={FieldCheckboxRenderer} {...props} />;

export default FieldCheckboxGroup;
